<template>
  <div class="modal fade" id="osSpoofingModal" tabindex="-1" aria-hidden="true" ref="modalEl">
      <div class="modal-dialog modal-dialog-centered w-[400px]">
          <div class="modal-content" v-if="props.license">
              <div class="modal-header">
                  <h1 class="modal-title fs-5">{{ isPp() ? 'OS Spoofing and MTU' : 'OS Spoofing' }}</h1>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <div v-if="isPp()" class="px-2 pb-1 text-[13px] osm-dim">OS fingerprint</div>
                <div class="float-left w-full px-2 py-2">
                  <v-select v-model="selectedOSType" :items="types" density="compact" item-title="name" rounded="0"
                  variant="outlined" style="padding-top: 0px;padding-bottom: 0px;">
                      <template v-slot:selection="{ item }"  >
                          <v-list-item :title="item.raw.name" density style="padding: 0;">
                          <template v-slot:prepend>
                              {{item.name}}
                          </template>
                          </v-list-item>
                      </template>
                      <template v-slot:item="{ props, item }">
                          <v-list-item v-bind="props" :title="item.raw.name"   density="compact">
                          <template v-slot:prepend>
                              {{ item.name }}
                          </template>
                          </v-list-item>
                      </template>
                  </v-select>
              </div>
                <!-- ProProxyUI: the MTU sites see, only the values this modem's carrier can carry -->
                <div v-if="isPp() && mtu" class="float-left w-full px-2 pb-2 flex flex-col gap-2">
                  <div>
                    <div class="pb-1 text-[13px] osm-dim">MTU seen by sites</div>
                    <v-select v-model="mtuValue" :items="mtuItems" item-title="name" item-value="value" density="compact" rounded="0" variant="outlined" hide-details></v-select>
                    <div class="pt-1 text-[12px] osm-dim">{{ mtu.carrier ? `This modem's carrier allows up to ${mtu.carrier}` : 'Carrier MTU not measured yet: use Automatic for now' }}</div>
                  </div>
                  <div class="osm-seen"><span class="osm-dim">Sites see</span><b>{{ seenLine }}</b></div>
                  <div class="text-[12px] osm-dim">Open connections restart when this changes.</div>
                </div>
              </div>
              <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                  <button @click="onUpdate" :disabled="buttonDisableFlag" class="btn btn-primary">Update</button>
              </div>
          </div>
      </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watchEffect, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useProxyStore } from "@/stores"

const buttonDisableFlag = ref(false)
const proxyStore = useProxyStore();

const props = defineProps({
  license: {
      type: Object,
      required: true
  }
});
const emit = defineEmits(['inInput', 'submit'])

const proxySmartTypes = [
    {value: '', name: '--No Spoofing--'}, 
    {value: 'android:1', name: 'android:1, p0f compliand but slow'},
    {value: 'android:3', name: 'android:3, real Android, almost like Linux'},
    {value: 'android:4', name: 'android:4, real Android 14'},
    {value: 'macosx:3', name: 'macosx:3'},
    {value: 'macosx:4', name: 'macosx:4, real MacOSX 12.6 / iPhone 13 Pro Max'},
    {value: 'macosx:5', name: 'macosx:5, real MacOSX Ventura 13.5.1'},
    {value: 'ios:1', name: 'ios:1, p0f compliant'},
    {value: 'ios:2', name: 'ios:2, real lphone'},
    {value: 'ios:3', name: 'ios:3, real IPhone 12 max pro'},
    {value: 'windows:1', name: 'windows:1, real Windnows 10 64bit Server or Tiny'},
    {value: 'windows:4', name: 'windows:4, real Windows 10 and 11 64bit Desktop'}
];

const iProxyTypes = [
    { value: '', name: 'Android' },
    { value: 'WinXP', name: 'WinXP' },
    { value: 'Win78', name: 'Win78' },
    { value: 'Win10', name: 'Win10' },
    { value: 'Win11', name: 'Win11' },
    { value: 'WinNT', name: 'WinNT' },
    { value: 'Nintendo', name: 'Nintendo' },
    { value: 'FreeBSD', name: 'FreeBSD' },
    { value: 'FreeBSD9', name: 'FreeBSD9' },
    { value: 'MacOS', name: 'MacOS' },
    { value: 'iOS', name: 'iOS' }
];

// ProProxyUI: the profiles come from the box (GET /proxy/os-options), read each time this dialog opens
const isPp = () => props?.license?.m_type === 'PROPROXYUI'
const ppTypes = ref([])
const types = computed(() => props?.license?.m_type === 'IPROXY' ? iProxyTypes : isPp() ? ppTypes.value : proxySmartTypes);
const selectedOSType = ref({value: '', name: 'Android'})
watchEffect(() => {
  if (isPp()) return   // set by loadPp() below
  selectedOSType.value = props?.license
    ? (types.value.find(t => t.value === props?.license.os) || { value: '', name: 'Android' })
    : { value: '', name: 'Android' };
})
// ProProxyUI MTU: { current, carrier, options, profiles } from the server; 0 = match the fingerprint
const mtu = ref(null)
const mtuValue = ref(0)
const mtuItems = computed(() => [{ value: 0, name: 'Automatic (matches the OS)' }, ...((mtu.value && mtu.value.options) || [])])
const seenLine = computed(() => {
  const m = mtu.value; if (!m) return ''
  const chosen = selectedOSType.value
  // the dropdown holds the list item until another is picked, then just its value
  const os = (chosen && typeof chosen === 'object') ? chosen : (ppTypes.value.find(t => t.value === chosen) || { value: '', name: '' })
  const carrier = m.carrier || 0
  let size = Number(mtuValue.value) || 0
  if (!size) { const p = os.value ? (m.profiles || {})[os.value] : 0; size = p ? (carrier ? Math.min(p, carrier) : p) : carrier }   // phones and native: the carrier MTU
  return `${os.value ? os.name : 'Native (no spoofing)'} · ${size ? 'MTU ' + size : 'automatic MTU'}${Number(mtuValue.value) ? ' (set by you)' : ''}`
})
// Loads only while this dialog is open: the licence prop is shared with every other dialog on the page
const modalEl = ref(null)
const isOpen = () => !!modalEl.value && modalEl.value.classList.contains('show')
async function loadPp() {
  const license = props.license
  if (!license || !isPp()) return
  const loading = [{ value: '', name: 'Loading profiles from the server…' }]
  ppTypes.value = loading
  selectedOSType.value = loading[0]
  mtu.value = null
  const res = await proxyStore.getOsOptions(license.uuid)   // cached per licence in the store
  if (res && res.mtu) { mtu.value = res.mtu; mtuValue.value = Number(res.mtu.current) || 0 }
  if (res && res.options && res.options.length) {
    ppTypes.value = res.options
    selectedOSType.value = res.options.find(t => t.value === res.current) || res.options[0]
  } else {
    const failed = [{ value: '', name: 'Could not load profiles' }]
    ppTypes.value = failed
    selectedOSType.value = failed[0]
  }
}
watch(() => props.license, () => { if (isOpen()) loadPp() })
onMounted(() => modalEl.value && modalEl.value.addEventListener('shown.bs.modal', loadPp))
onBeforeUnmount(() => modalEl.value && modalEl.value.removeEventListener('shown.bs.modal', loadPp))
const onUpdate = () => {
  buttonDisableFlag.value = true;
  // ProProxyUI: just the profile's value, plus the MTU
  const chosen = selectedOSType.value
  const osType = isPp() ? ((chosen && typeof chosen === 'object') ? (chosen.value ?? '') : (chosen ?? '')) : selectedOSType.value
  proxyStore.changeOS(props.license.uuid, osType, isPp() && mtu.value ? Number(mtuValue.value) || 0 : undefined).then(() => {
      buttonDisableFlag.value = false;
      emit('close');
  });
};
</script>

<style scoped>
.osm-dim { color: #6b7280; }
.osm-seen { display: flex; justify-content: space-between; gap: 12px; padding: 8px 10px; border-radius: 8px; background: rgba(127, 127, 127, 0.12); font-size: 13px; }
.osm-seen b { font-weight: 500; text-align: right; }
</style>

<style>
body.dark .osm-dim { color: #bcbbc7; }
body.dark .osm-seen { background: #45445e; }
</style>
